/**
 * Honeylyn Labor Day — shared campaign JS
 * Namespace: window.HlyLd
 * Handles: countdown math, popup behavior, announcement bar behavior.
 * No external dependencies. Safe to load once per page even if both
 * the popup and the bar sections are present.
 */
(function () {
  if (window.HlyLd) return; // guard against double-init if a section renders twice

  var HlyLd = {};

  /* -----------------------------------------------------------
   * Shared countdown core
   * Both the popup and the announcement bar call this with the
   * SAME end-date settings, so they can never show different times.
   * --------------------------------------------------------- */
  HlyLd.getTargetDate = function (dateStr, timeStr, offset) {
    // dateStr: "2026-09-07", timeStr: "23:59:59", offset: "-04:00"
    if (!dateStr) return null;
    var t = timeStr || '23:59:59';
    var off = offset || '-04:00';
    var iso = dateStr + 'T' + t + off;
    var d = new Date(iso);
    return isNaN(d.getTime()) ? null : d;
  };

  HlyLd.getRemaining = function (targetDate) {
    if (!targetDate) return { expired: true, days: 0, hours: 0, minutes: 0, seconds: 0 };
    var diff = targetDate.getTime() - Date.now();
    if (diff <= 0) return { expired: true, days: 0, hours: 0, minutes: 0, seconds: 0 };
    var days = Math.floor(diff / 86400000);
    var hours = Math.floor((diff % 86400000) / 3600000);
    var minutes = Math.floor((diff % 3600000) / 60000);
    var seconds = Math.floor((diff % 60000) / 1000);
    return { expired: false, days: days, hours: hours, minutes: minutes, seconds: seconds };
  };

  HlyLd.pad = function (n) {
    return String(n).padStart(2, '0');
  };

  /**
   * Starts an interval that recalculates from Date.now() every tick
   * (never just decrements), so it self-corrects after a backgrounded
   * tab or a slow device. Returns a stop() function.
   */
  HlyLd.runCountdown = function (targetDate, onTick, onExpire) {
    var expiredFired = false;
    function tick() {
      var r = HlyLd.getRemaining(targetDate);
      onTick(r);
      if (r.expired && !expiredFired) {
        expiredFired = true;
        onExpire();
        clearInterval(timer);
      }
    }
    tick();
    var timer = setInterval(tick, 1000);
    return function stop() {
      clearInterval(timer);
    };
  };

  /* -----------------------------------------------------------
   * Popup
   * --------------------------------------------------------- */
  HlyLd.initPopup = function (root) {
    if (!root || root.dataset.hlyLdInit) return;
    root.dataset.hlyLdInit = 'true';

    var cfg = JSON.parse(root.querySelector('[data-hly-ld-config]').textContent);
    var overlay = root.querySelector('.hly-ld-overlay');
    var dialog = root.querySelector('.hly-ld-popup');
    var closeBtns = root.querySelectorAll('[data-hly-ld-close]');
    var countdownWrap = root.querySelector('.hly-ld-countdown');
    var expiredEl = root.querySelector('.hly-ld-expired');
    var copyBtn = root.querySelector('[data-hly-ld-copy]');
    var lastFocused = null;
    var stopCountdown = null;
    var scrollY = 0;

    if (!cfg.enabled) return;

    var target = HlyLd.getTargetDate(cfg.endDate, cfg.endTime, cfg.tzOffset);

    // Already expired at load and configured to hide entirely — never show.
    if (cfg.hideAfterExpiration && HlyLd.getRemaining(target).expired) return;

    function storageKeyFrequency() {
      return 'hlyLdPopupShown_' + cfg.sectionId;
    }

    function frequencyBlocksShow() {
      var freq = cfg.frequency;
      if (freq === 'every_load') return false;

      if (freq === 'once_session') {
        return sessionStorage.getItem(storageKeyFrequency()) === '1';
      }

      var days = { once_day: 1, once_3days: 3, once_7days: 7 }[freq];
      if (!days) return false;
      var last = localStorage.getItem(storageKeyFrequency());
      if (!last) return false;
      var elapsedMs = Date.now() - parseInt(last, 10);
      return elapsedMs < days * 86400000;
    }

    function markShown() {
      if (cfg.frequency === 'once_session') {
        sessionStorage.setItem(storageKeyFrequency(), '1');
      } else if (cfg.frequency !== 'every_load') {
        localStorage.setItem(storageKeyFrequency(), String(Date.now()));
      }
    }

    function lockScroll() {
      scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = '-' + scrollY + 'px';
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.classList.add('hly-ld-scroll-locked');
    }

    function unlockScroll() {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.classList.remove('hly-ld-scroll-locked');
      window.scrollTo(0, scrollY);
    }

    function openPopup(trigger) {
      if (root.classList.contains('is-open')) return;
      lastFocused = trigger || document.activeElement;
      root.hidden = false;
      // force reflow so the transition runs
      void root.offsetWidth;
      root.classList.add('is-open');
      lockScroll();
      markShown();
      dialog.setAttribute('tabindex', '-1');
      dialog.focus();
      document.addEventListener('keydown', onKeydown);
    }

    function closePopup() {
      if (!root.classList.contains('is-open')) return;
      root.classList.remove('is-open');
      unlockScroll();
      document.removeEventListener('keydown', onKeydown);
      var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      var delay = reduceMotion ? 0 : 350;
      setTimeout(function () {
        root.hidden = true;
      }, delay);
      if (lastFocused && typeof lastFocused.focus === 'function') {
        lastFocused.focus();
      }
    }

    function onKeydown(e) {
      if (e.key === 'Escape') {
        closePopup();
        return;
      }
      if (e.key === 'Tab') {
        var focusable = dialog.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])');
        if (!focusable.length) return;
        var first = focusable[0];
        var last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    closeBtns.forEach(function (btn) {
      btn.addEventListener('click', closePopup);
    });

    if (cfg.closeOnOutsideClick && overlay) {
      overlay.addEventListener('click', function (e) {
        if (e.target === overlay) closePopup();
      });
    }

    if (copyBtn && cfg.discountCode) {
      copyBtn.addEventListener('click', function () {
        navigator.clipboard.writeText(cfg.discountCode).then(function () {
          var original = copyBtn.textContent;
          copyBtn.textContent = 'COPIED';
          copyBtn.disabled = true;
          setTimeout(function () {
            copyBtn.textContent = original;
            copyBtn.disabled = false;
          }, 2000);
        });
      });
    }

    // Countdown rendering
    if (cfg.countdownEnabled && countdownWrap && target) {
      var dEl = countdownWrap.querySelector('[data-unit="days"]');
      var hEl = countdownWrap.querySelector('[data-unit="hours"]');
      var mEl = countdownWrap.querySelector('[data-unit="minutes"]');
      var sEl = countdownWrap.querySelector('[data-unit="seconds"]');

      stopCountdown = HlyLd.runCountdown(
        target,
        function (r) {
          if (dEl) dEl.textContent = HlyLd.pad(r.days);
          if (hEl) hEl.textContent = HlyLd.pad(r.hours);
          if (mEl) mEl.textContent = HlyLd.pad(r.minutes);
          if (sEl) sEl.textContent = HlyLd.pad(r.seconds);
        },
        onExpire
      );
    }

    function onExpire() {
      if (cfg.hideAfterExpiration) {
        closePopup();
        root.remove();
        return;
      }
      if (countdownWrap) countdownWrap.hidden = true;
      if (expiredEl) expiredEl.hidden = false;
    }

    // Decide whether we're even allowed to show
    if (frequencyBlocksShow()) return;

    // Delay-based show (mobile + desktop default path)
    var isDesktop = window.matchMedia('(min-width: 850px)').matches;
    var delayTimer = null;

    if (cfg.delaySeconds >= 0) {
      delayTimer = setTimeout(function () {
        openPopup();
      }, cfg.delaySeconds * 1000);
    }

    // Exit intent (desktop only, per spec)
    if (cfg.exitIntentEnabled && isDesktop) {
      var exitHandler = function (e) {
        if (e.clientY <= 0 && !root.classList.contains('is-open') && !frequencyBlocksShow()) {
          if (delayTimer) clearTimeout(delayTimer);
          openPopup();
          document.removeEventListener('mouseout', exitHandler);
        }
      };
      document.addEventListener('mouseout', exitHandler);
    }
  };

  /* -----------------------------------------------------------
   * Announcement bar
   * --------------------------------------------------------- */
  HlyLd.initBar = function (root) {
    if (!root || root.dataset.hlyLdInit) return;
    root.dataset.hlyLdInit = 'true';

    var cfg = JSON.parse(root.querySelector('[data-hly-ld-config]').textContent);
    if (!cfg.enabled) return;

    var target = HlyLd.getTargetDate(cfg.endDate, cfg.endTime, cfg.tzOffset);

    if (cfg.hideAfterExpiration && HlyLd.getRemaining(target).expired) {
      root.remove();
      return;
    }

    var desktopEl = root.querySelector('.hly-ld-bar-text--desktop');
    var mobileEl = root.querySelector('.hly-ld-bar-text--mobile');

    function render(r) {
      var vals = {
        '{days}': r.days,
        '{hours}': HlyLd.pad(r.hours),
        '{minutes}': HlyLd.pad(r.minutes),
        '{seconds}': HlyLd.pad(r.seconds)
      };
      function fill(tpl) {
        return tpl.replace(/\{days\}|\{hours\}|\{minutes\}|\{seconds\}/g, function (m) {
          return vals[m];
        });
      }
      if (desktopEl) desktopEl.textContent = fill(cfg.textDesktop);
      if (mobileEl) mobileEl.textContent = fill(cfg.textMobile);
    }

    if (cfg.countdownEnabled && target) {
      HlyLd.runCountdown(target, render, function () {
        if (cfg.hideAfterExpiration) {
          root.remove();
        } else {
          if (desktopEl) desktopEl.textContent = cfg.expiredText;
          if (mobileEl) mobileEl.textContent = cfg.expiredText;
        }
      });
    } else {
      if (desktopEl) desktopEl.textContent = cfg.textDesktop.replace(/\{[a-z]+\}/g, '').trim();
      if (mobileEl) mobileEl.textContent = cfg.textMobile.replace(/\{[a-z]+\}/g, '').trim();
    }
  };

  window.HlyLd = HlyLd;

  // Auto-init anything already on the page (sections render their own
  // <script> that calls these, but this covers direct markup use too).
  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('[data-hly-ld-popup]').forEach(HlyLd.initPopup);
    document.querySelectorAll('[data-hly-ld-bar]').forEach(HlyLd.initBar);
  });
})();